/**
 * What you can do to a message — V7-04 §3, §4, §5.
 *
 * One hover row rather than four separate affordances, because a message is
 * small and every button on it competes with the text. The order is by how
 * often it is used: react, reply in thread, save, pin, make a task.
 *
 * Reactions already on the message render underneath as chips, which is the
 * only part of this that is visible without hovering — a reaction nobody can
 * see until they hover is a reaction nobody sends.
 */

import { useState } from 'react';
import { Bookmark, ListPlus, MessageSquare, Pin, SmilePlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { ChatMessageView } from './api';

/**
 * The same eight the server accepts. A picker offering more than the API takes
 * is a picker that fails on the ninth choice.
 */
export const QUICK_REACTIONS = ['👍', '❤️', '😂', '🎉', '👀', '🙏', '✅', '😮'] as const;

export interface MessageActionsProps {
  message: ChatMessageView;
  /** §2 — absent in a DM and inside a thread pane; threads do not nest. */
  onOpenThread?: () => void;
  onReact: (emoji: string) => void;
  onToggleSaved: () => void;
  /** §5 — absent unless this person may pin here. */
  onTogglePin?: () => void;
  /** §4 — absent for a message with no text to make a task from. */
  onCreateTask?: () => void;
}

export function MessageActions({
  message,
  onOpenThread,
  onReact,
  onToggleSaved,
  onTogglePin,
  onCreateTask,
}: MessageActionsProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const isPinned = Boolean(message.pinnedAt);
  const isSaved = Boolean(message.saved);

  return (
    /**
     * Chrome-free since V9-01 §2: the surface, the elevation and the reveal now
     * belong to `MessageToolbar`, which floats this over the bubble instead of
     * standing it in the row. A second surface inside that one would draw a box
     * inside a box, and a second `opacity-0` would fight the first.
     */
    <div className="flex items-center gap-0.5" data-testid="chat-message-actions-group">
      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label="Add reaction" data-testid="chat-react">
            <SmilePlus />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-1" data-testid="chat-reaction-picker">
          <div className="flex gap-0.5">
            {QUICK_REACTIONS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                aria-label={`React ${emoji}`}
                data-testid="chat-reaction-option"
                data-emoji={emoji}
                className="rounded-[6px] px-1.5 py-1 text-[16px] hover:bg-[var(--surface-2)]"
                onClick={() => {
                  setPickerOpen(false);
                  onReact(emoji);
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {onOpenThread && (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Reply in thread"
          data-testid="chat-open-thread"
          onClick={onOpenThread}
        >
          <MessageSquare />
        </Button>
      )}

      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={isSaved ? 'Remove from saved' : 'Save message'}
        data-testid="chat-save"
        data-saved={isSaved ? 'true' : 'false'}
        onClick={onToggleSaved}
      >
        <Bookmark className={cn(isSaved && 'fill-current text-[var(--accent)]')} />
      </Button>

      {onTogglePin && (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={isPinned ? 'Unpin message' : 'Pin message'}
          data-testid="chat-pin"
          data-pinned={isPinned ? 'true' : 'false'}
          onClick={onTogglePin}
        >
          <Pin className={cn(isPinned && 'fill-current text-[var(--accent)]')} />
        </Button>
      )}

      {onCreateTask && (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Create task from message"
          data-testid="chat-create-task"
          onClick={onCreateTask}
        >
          <ListPlus />
        </Button>
      )}
    </div>
  );
}

/**
 * The reactions already on a message — §3.
 *
 * Clicking a chip toggles your own, which is why the count and the highlight
 * both have to be right: a chip that says 3 and does not include you must add
 * you, and one that includes you must remove you. The server answers `mine` so
 * neither is a guess.
 */
export function ReactionChips({
  message,
  onReact,
}: {
  message: ChatMessageView;
  onReact: (emoji: string) => void;
}) {
  const reactions = message.reactions ?? [];
  if (reactions.length === 0) return null;

  return (
    <div className="mt-1 flex flex-wrap gap-1" data-testid="chat-reactions">
      {reactions.map((reaction) => (
        <Tooltip key={reaction.emoji}>
          <TooltipTrigger asChild>
            <button
              type="button"
              data-testid="chat-reaction"
              data-emoji={reaction.emoji}
              data-mine={reaction.mine ? 'true' : 'false'}
              aria-label={`${reaction.emoji} ${reaction.count}`}
              className={cn(
                'flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px]',
                reaction.mine
                  ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--accent)]'
                  : 'border-[var(--border)] vt-surface',
              )}
              onClick={() => onReact(reaction.emoji)}
            >
              <span>{reaction.emoji}</span>
              <span className="tabular-nums">{reaction.count}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent>{namesFor(reaction.names)}</TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
}

/** "Ada", "Ada and Bo", "Ada, Bo and 3 others" — never a wall of names. */
function namesFor(names: string[]): string {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} other${names.length === 3 ? '' : 's'}`;
}

/**
 * The "3 replies" affordance under a message that has a thread — §2.
 *
 * This is the *only* way into a thread from the main scroll, and the only place
 * a reader learns one exists. Without it, replies would be invisible rather
 * than merely out of the scroll, which is not the same thing at all.
 */
export function ThreadSummary({
  message,
  onOpen,
}: {
  message: ChatMessageView;
  onOpen: () => void;
}) {
  const count = message.replyCount ?? 0;
  if (count === 0) return null;

  return (
    <button
      type="button"
      data-testid="chat-thread-summary"
      data-reply-count={count}
      className="mt-1 flex items-center gap-1 rounded-[6px] px-1.5 py-0.5 text-[11px] font-medium text-[var(--accent)] hover:bg-[var(--surface-2)]"
      onClick={onOpen}
    >
      <MessageSquare className="size-3" />
      {count} {count === 1 ? 'reply' : 'replies'}
      {message.lastReplyAt && (
        <span className="font-normal vt-muted">· last {sinceLabel(message.lastReplyAt)}</span>
      )}
    </button>
  );
}

/**
 * "2h ago" — §2's half of "3 replies · last 2h ago".
 *
 * How stale a thread is decides whether it is worth opening, and a bare count
 * cannot say. Coarse on purpose: nobody needs the seconds, and a label that
 * re-renders every second would be a timer per message on screen.
 */
function sinceLabel(iso: string): string {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days < 7 ? `${days}d ago` : `${Math.floor(days / 7)}w ago`;
}
